import { useState, useEffect, useMemo, useCallback } from 'react';
import { VALID_STATUSES } from '../../../../firebase/leads.service';

/**
 * Hook for managing trigger configuration state.
 * Handles both 'form' and 'manual' entry types.
 * 
 * @param {Object} options
 * @param {Object} options.initialConfig - Initial trigger config from saved automation
 * @param {Array} options.leads - Array of leads data from Firestore
 * @param {Function} options.onConfigChange - Callback when config changes
 * @returns {Object} State and handlers for trigger configuration
 */
const useTriggerConfigState = ({ initialConfig = {}, leads = [], onConfigChange } = {}) => {
    // Entry type: 'form' or 'manual'
    const [entryType, setEntryType] = useState(initialConfig?.entryType || 'form');

    // Form-based trigger config
    const [selectedForm, setSelectedForm] = useState(initialConfig?.formId || '');

    // Manual entry config - filters
    const [selectedTags, setSelectedTags] = useState(initialConfig?.leadFilters?.tags || []);
    const [selectedStatus, setSelectedStatus] = useState(initialConfig?.leadFilters?.status || 'All');

    // Manual entry config - individual lead selection
    const [selectedLeadIds, setSelectedLeadIds] = useState(initialConfig?.selectedLeadIds || []);

    // UI state
    const [searchQuery, setSearchQuery] = useState('');
    const [showLeadsPreview, setShowLeadsPreview] = useState(false);

    // Initialize from config when it changes (e.g., opening existing automation)
    useEffect(() => {
        if (initialConfig) {
            setEntryType(initialConfig.entryType || 'form');
            setSelectedForm(initialConfig.formId || '');
            setSelectedTags(initialConfig.leadFilters?.tags || []);
            setSelectedStatus(initialConfig.leadFilters?.status || 'All');
            setSelectedLeadIds(initialConfig.selectedLeadIds || []);
            setSearchQuery('');
            // NOTE: Do NOT reset showLeadsPreview here.
            // This effect re-runs on every parent config update (feedback loop),
            // so resetting UI visibility state here would close the panel
            // whenever a checkbox is toggled.
        }
    }, [initialConfig]);

    // Extract unique tags from leads (similar to useLeadTags hook pattern)
    const availableTags = useMemo(() => {
        const tagSet = new Set();
        leads.forEach(lead => {
            if (Array.isArray(lead.tags)) {
                lead.tags.forEach(tag => tagSet.add(tag));
            }
        });
        return Array.from(tagSet).sort();
    }, [leads]);

    // Get filtered leads based on current filters (same logic as Broadcast)
    const getFilteredLeads = useCallback(() => {
        let filtered = [...leads];

        // Filter by status
        if (selectedStatus && selectedStatus !== 'All') {
            filtered = filtered.filter(lead => lead.status === selectedStatus);
        }

        // Filter by tags (lead must have ALL selected tags)
        if (selectedTags.length > 0) {
            filtered = filtered.filter(lead => {
                const leadTags = lead.tags || [];
                return selectedTags.every(selectedTag =>
                    leadTags.some(leadTag =>
                        leadTag.toLowerCase() === selectedTag.toLowerCase() ||
                        leadTag.toLowerCase() === selectedTag.toLowerCase().replace('#', '') ||
                        `#${leadTag}`.toLowerCase() === selectedTag.toLowerCase()
                    )
                );
            });
        }

        return filtered;
    }, [leads, selectedStatus, selectedTags]);

    // Get individually selected leads
    const getSelectedLeads = useCallback(() => {
        return leads.filter(lead => selectedLeadIds.includes(lead.id));
    }, [leads, selectedLeadIds]);

    // Get all matching leads (filtered + individually selected, deduplicated)
    // Used only for preview display in View Matching panel
    const getAllMatchingLeads = useCallback(() => {
        const filteredLeads = getFilteredLeads();
        const individualLeads = getSelectedLeads();

        // Merge and deduplicate by lead ID
        const leadMap = new Map();
        filteredLeads.forEach(lead => leadMap.set(lead.id, lead));
        individualLeads.forEach(lead => leadMap.set(lead.id, lead));

        return Array.from(leadMap.values());
    }, [getFilteredLeads, getSelectedLeads]);

    // Selected count: ONLY explicitly checked leads (this is what gets enrolled)
    const selectedCount = useMemo(() => {
        return selectedLeadIds.length;
    }, [selectedLeadIds]);

    // Filtered preview count: leads matching current filters (for preview label)
    const filteredCount = useMemo(() => {
        return getFilteredLeads().length;
    }, [getFilteredLeads]);

    // Tag management
    const addTag = useCallback((tag) => {
        if (!selectedTags.includes(tag)) {
            setSelectedTags(prev => [...prev, tag]);
        }
    }, [selectedTags]);

    const removeTag = useCallback((tagToRemove) => {
        setSelectedTags(prev => prev.filter(t => t !== tagToRemove));
    }, []);

    // Individual lead selection
    const toggleLeadSelection = useCallback((leadId) => {
        setSelectedLeadIds(prev => {
            if (prev.includes(leadId)) {
                return prev.filter(id => id !== leadId);
            } else {
                return [...prev, leadId];
            }
        });
    }, []);

    const isLeadSelected = useCallback((leadId) => {
        return selectedLeadIds.includes(leadId);
    }, [selectedLeadIds]);

    // Select all currently filtered leads
    const selectAllFilteredLeads = useCallback(() => {
        const filtered = getFilteredLeads();
        const filteredIds = filtered.map(lead => lead.id);
        setSelectedLeadIds(prev => {
            const newSet = new Set([...prev, ...filteredIds]);
            return Array.from(newSet);
        });
    }, [getFilteredLeads]);

    // Deselect all currently filtered leads
    const deselectAllFilteredLeads = useCallback(() => {
        const filtered = getFilteredLeads();
        const filteredIds = new Set(filtered.map(lead => lead.id));
        setSelectedLeadIds(prev => prev.filter(id => !filteredIds.has(id)));
    }, [getFilteredLeads]);

    // Search leads by name, email, or phone
    const searchedLeads = useMemo(() => {
        if (!searchQuery.trim()) return [];

        const query = searchQuery.toLowerCase();
        return leads.filter(lead => {
            const name = (lead.fullName || lead.name || '').toLowerCase();
            const email = (lead.email || '').toLowerCase();
            const phone = (lead.phone || '').toLowerCase();
            return name.includes(query) || email.includes(query) || phone.includes(query);
        }).slice(0, 10); // Limit search results
    }, [leads, searchQuery]);

    // Notify parent of config changes
    useEffect(() => {
        if (onConfigChange) {
            const config = {
                entryType,
                formId: entryType === 'form' ? selectedForm : null,
                leadFilters: {
                    tags: selectedTags,
                    status: selectedStatus,
                },
                selectedLeadIds,
                // resolvedLeadCount reflects ONLY explicitly selected leads
                resolvedLeadCount: selectedLeadIds.length,
            };
            onConfigChange(config);
        }
    }, [entryType, selectedForm, selectedTags, selectedStatus, selectedLeadIds, onConfigChange]);

    return {
        // Entry type
        entryType,
        setEntryType,

        // Form selection
        selectedForm,
        setSelectedForm,

        // Filter state
        selectedTags,
        setSelectedTags,
        addTag,
        removeTag,
        selectedStatus,
        setSelectedStatus,

        // Individual selection
        selectedLeadIds,
        setSelectedLeadIds,
        toggleLeadSelection,
        isLeadSelected,
        selectAllFilteredLeads,
        deselectAllFilteredLeads,

        // Search
        searchQuery,
        setSearchQuery,
        searchedLeads,

        // Leads data
        availableTags,
        availableStatuses: ['All', ...VALID_STATUSES],
        getFilteredLeads,
        getSelectedLeads,
        getAllMatchingLeads,
        selectedCount,
        filteredCount,

        // UI state
        showLeadsPreview,
        setShowLeadsPreview,
    };
};

export default useTriggerConfigState;
